import {
  Badge,
  Flex,
  Icon,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { ViewIcon } from "@chakra-ui/icons";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import {
  AuctionProposalSortDirection,
  ResponseAuctionProposalDto,
} from "@/models/credit-auction-proposal.model";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { documentFormatter } from "@/utils/documentFormatter";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  AUCTION_PROPOSAL_STATUS_COLORS,
  AUCTION_PROPOSAL_STATUS_LABELS,
  deriveNegotiationSubStep,
  getAuctionTaxType,
} from "@/utils/auctionProposalStatus";
import StatusSteps from "./status-steps";

interface CreditAuctionProposalsTableProps {
  proposals: ResponseAuctionProposalDto[];
  onOpenDetails: (proposal: ResponseAuctionProposalDto) => void;
  validadeSort: AuctionProposalSortDirection | null;
  onToggleValidadeSort: () => void;
}

const tableHeaderStyles = {
  color: "white",
  textAlign: "center" as const,
  fontSize: "10px",
  py: 5,
  px: 1,
  fontFamily: "Lexend Deca",
  _notLast: {
    borderRight: "1px solid",
    borderColor: "whiteAlpha.400",
  },
};

const tableCellStyles = {
  textAlign: "center" as const,
  fontFamily: "Lexend Deca",
  fontSize: "12px",
  fontWeight: "normal" as const,
  px: 1,
};

const EDGE_COLUMN_PX = 4;

const COLUMN_WIDTHS = {
  acoes: "56px",
  cpf: "120px",
  valorLiberado: "110px",
  parcelas: "70px",
  margem: "100px",
  taxaMensal: "90px",
  validade: "150px",
  criacao: "120px",
  status: "120px",
  progresso: "280px",
};

function formatValidadeDate(raw: string | null): string {
  if (!raw) return "-";
  // Formato 14 dígitos DDMMYYYYHHMMSS — exibe como a Criação (dd/mm/aa hh:mm).
  if (/^\d{14}$/.test(raw)) {
    const day = raw.slice(0, 2);
    const month = raw.slice(2, 4);
    const year = raw.slice(6, 8);
    const hour = raw.slice(8, 10);
    const min = raw.slice(10, 12);
    return `${day}/${month}/${year} ${hour}:${min}`;
  }
  // Demais formatos (ISO vindo da API) — mesma exibição da coluna Criação.
  return formatCreatedAt(raw);
}

function formatCreatedAt(raw: string | null): string {
  if (!raw) return "-";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CreditAuctionProposalsTable({
  proposals,
  onOpenDetails,
  validadeSort,
  onToggleValidadeSort,
}: CreditAuctionProposalsTableProps) {
  const backgroundStyle = useConditionalBackground();

  const validadeSortIcon =
    validadeSort === "asc"
      ? FaSortUp
      : validadeSort === "desc"
      ? FaSortDown
      : FaSort;
  const validadeSortTooltip =
    validadeSort === "asc"
      ? "Ordenado: mais próximas primeiro (clique para inverter)"
      : validadeSort === "desc"
      ? "Ordenado: mais distantes primeiro (clique para limpar)"
      : "Ordenar por validade";

  return (
    <TableContainer borderRadius="lg" overflowX="auto">
      <Table variant="simple">
        <Thead {...backgroundStyle}>
          <Tr>
            <Th {...tableHeaderStyles} w={COLUMN_WIDTHS.acoes} px={EDGE_COLUMN_PX}>
              AÇÕES
            </Th>
            <Th {...tableHeaderStyles} w={COLUMN_WIDTHS.cpf}>CPF</Th>
            <Th {...tableHeaderStyles} w={COLUMN_WIDTHS.valorLiberado}>
              VALOR LIBERADO
            </Th>
            <Th {...tableHeaderStyles} w={COLUMN_WIDTHS.parcelas}>PARCELAS</Th>
            <Th {...tableHeaderStyles} w={COLUMN_WIDTHS.margem}>MARGEM</Th>
            <Th {...tableHeaderStyles} w={COLUMN_WIDTHS.taxaMensal}>
              TAXA MENSAL
            </Th>
            <Th {...tableHeaderStyles} w={COLUMN_WIDTHS.criacao}>CRIAÇÃO</Th>
            <Th
              {...tableHeaderStyles}
              w={COLUMN_WIDTHS.validade}
              cursor="pointer"
              userSelect="none"
              onClick={onToggleValidadeSort}
              _hover={{ opacity: 0.8 }}
            >
              <Tooltip label={validadeSortTooltip} placement="top" hasArrow>
                <Flex align="center" justify="center" gap={1.5}>
                  VALIDADE
                  <Icon as={validadeSortIcon} boxSize={3} />
                </Flex>
              </Tooltip>
            </Th>
            <Th {...tableHeaderStyles} w={COLUMN_WIDTHS.status}>STATUS</Th>
            <Th
              {...tableHeaderStyles}
              w={COLUMN_WIDTHS.progresso}
              px={EDGE_COLUMN_PX}
            >
              PROGRESSO CONTRATAÇÃO
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {proposals.map((proposal) => (
            <Tr key={proposal.id} _odd={{ bg: "blackAlpha.50" }}>
              <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                <Tooltip label="Ver Detalhes" placement="top">
                  <IconButton
                    aria-label="Ver detalhes da proposta"
                    icon={<ViewIcon />}
                    size="sm"
                    variant="ghost"
                    colorScheme="blue"
                    onClick={() => onOpenDetails(proposal)}
                  />
                </Tooltip>
              </Td>
              <Td {...tableCellStyles}>
                {documentFormatter(proposal.documentoTomador)}
              </Td>
              <Td {...tableCellStyles}>
                {proposal.valorLiberado != null
                  ? BrlCurrencyFormatter(proposal.valorLiberado)
                  : "-"}
              </Td>
              <Td {...tableCellStyles}>{proposal.numeroParcelas ?? "-"}</Td>
              <Td {...tableCellStyles}>
                {proposal.margem != null
                  ? BrlCurrencyFormatter(proposal.margem)
                  : "-"}
              </Td>
              <Td {...tableCellStyles}>
                {getAuctionTaxType(proposal.statusCustomizado) === "Reduzida"
                  ? "3.19%"
                  : proposal.valorTaxaMensal != null
                    ? `${ (proposal.valorTaxaMensal * 100).toFixed(1)}%`
                    : "-"}
              </Td>
              <Td {...tableCellStyles}>{formatCreatedAt(proposal.createdAt)}</Td>
              <Td {...tableCellStyles}>
                {formatValidadeDate(proposal.dataHoraValidadeSolicitacao)}
              </Td>
              <Td {...tableCellStyles}>
                <Badge
                  colorScheme={AUCTION_PROPOSAL_STATUS_COLORS[proposal.status] ?? "gray"}
                  borderRadius="md"
                  px={2}
                  py={1}
                  fontFamily="Lexend Deca"
                  fontSize="10px"
                  fontWeight="normal"
                  textAlign="center"
                  maxW="112px"
                  sx={{ whiteSpace: "normal" }}
                >
                  {AUCTION_PROPOSAL_STATUS_LABELS[proposal.status] ?? proposal.status}
                </Badge>
              </Td>
              <Td {...tableCellStyles} px={EDGE_COLUMN_PX}>
                <StatusSteps
                  currentStatus={proposal.status}
                  negotiationSubStep={
                    deriveNegotiationSubStep(proposal.statusCustomizado) ??
                    proposal.negotiationSubStep
                  }
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
